/**
 * The search page's decisions. **Pure**, so every case is checkable without a
 * DOM - same split as `palette-view.ts`, `header-view.ts` and `panel-view.ts`.
 *
 * ⛔ **INBOX scope item 22.**
 */
/**
 * ⚠️ THE TYPE COMES FROM THE SERVICE, NOT FROM `data/inbox-api.js`, and the
 * difference is not stylistic: this module is typechecked by the SERVER project
 * too, which has no `DOM` lib. Importing the web module pulls
 * `list-rendered-mark.ts` and its `PerformanceNavigationTiming` in behind it and
 * the pure test stops compiling. `palette-view.ts` takes it from the same place
 * for the same reason.
 */
import type { ContactHit } from '../../../src/services/contact-search.js'

/**
 * ┌─ ⛔ THREE OF THE SEVEN FACETS `Build_Order:491` NAMES. THE OTHER FOUR HAVE ─┐
 * │ NO STORAGE, checked against `information_schema` 2026-08-14: **zero tables │
 * │ match `booking|pipeline|stage|custom|field`.** Booking stage, pipeline,    │
 * │ booking date and custom field values are CRM-BUILD's.                      │
 * │                                                                            │
 * │ ⚠️ THIS IS THE ITEM NAMING THINGS A LATER PHASE OWNS, which is a different │
 * │ statement from reducing scope - the same treatment as the palette's `tag`  │
 * │ and `add task` and the right panel's booking blocks.                       │
 * └────────────────────────────────────────────────────────────────────────────┘
 */
export type FacetKey = 'date' | 'channel' | 'assignee'

export const BUILDABLE_FACETS: readonly FacetKey[] = ['date', 'channel', 'assignee']

/**
 * ⛔ NAMED, NOT LISTED AS "COMING SOON". A facet chip for something with no
 * storage would be a control that cannot filter - the empty-card failure item 11
 * refuses. These exist so the gap is stated in one place a reader can find, not
 * so anything renders them.
 */
export const FACETS_AWAITING_CRM_BUILD: readonly string[] = [
  'booking stage',
  'pipeline',
  'booking date',
  'custom field values',
]

/**
 * ⛔ THE TYPE CHIPS, AND `messages` IS NOT A DEFAULT. `Build_Order:487` gives
 * the page type filter chips WITH COUNTS; the counts come from the server, and
 * until both queries have run neither chip can claim a number.
 *
 * ⚠️ `both` EXISTS SO "NO FILTER" IS A VALUE RATHER THAN A NULL. The same rule
 * the list's `view` follows: a filter that is absent and a filter that is
 * "everything" behave identically today and diverge the moment one gains a
 * default.
 */
export type ResultType = 'both' | 'messages' | 'contacts'

export type SearchState = {
  query: string
  type: ResultType
  facets: { after?: string; before?: string; channel?: string; assigneeId?: string }
}

/**
 * ⛔ WHICH QUERIES A GIVEN STATE NEEDS. Returned as a decision rather than
 * inferred at each call site, because "the contacts request is skipped when the
 * chip says messages" is the kind of thing that gets half-implemented - one
 * caller skips it and another does not, and the counts disagree.
 */
export function queriesFor(state: SearchState): { messages: boolean; contacts: boolean } {
  return {
    messages: state.type === 'both' || state.type === 'messages',
    contacts: state.type === 'both' || state.type === 'contacts',
  }
}

/**
 * ⛔ THE MINIMUM, MIRRORING THE SERVICES. Below it both routes answer an empty
 * list rather than searching, so asking is a round trip whose result is known
 * before it is sent.
 */
export const MIN_QUERY = 3

export type SearchEmpty =
  | { kind: 'idle' }
  | { kind: 'tooShort' }
  | { kind: 'searching' }
  | { kind: 'noMatches' }
  | { kind: 'hasResults' }

/**
 * ⛔ FIVE STATES, AND `idle` IS THE ONE THE PALETTE DID NOT NEED. A search PAGE
 * can be open with nothing typed - it is a destination, not an overlay - and
 * "you have not searched yet" is a different sentence from "your query is too
 * short". The palette has four because it cannot be open and untouched.
 */
export function searchEmpty(params: {
  query: string
  loading: boolean
  messageCount: number
  contactCount: number
}): SearchEmpty {
  if (params.messageCount + params.contactCount > 0) return { kind: 'hasResults' }
  const q = params.query.trim()
  if (q === '') return { kind: 'idle' }
  // ⚠️ SHORT OUTRANKS LOADING, for the palette's reason: a short query is short
  // whether or not a request is in flight, and "searching" promises an answer
  // that is never coming.
  if (q.length < MIN_QUERY) return { kind: 'tooShort' }
  if (params.loading) return { kind: 'searching' }
  return { kind: 'noMatches' }
}

/**
 * ⛔ A COUNT IS A NUMBER OR IT IS UNKNOWN. NEVER ZERO-AS-PLACEHOLDER.
 *
 * `Build_Order:487` wants counts on the chips. Before a query returns there is
 * no count, and rendering `0` would say "none found" about a search that has not
 * run - the third-outcome rule, on a chip.
 */
export type ChipCount = { kind: 'known'; n: number } | { kind: 'unknown' }

export function chipCount(params: {
  enabled: boolean
  loaded: boolean
  n: number
}): ChipCount {
  // ⚠️ A DISABLED QUERY HAS NO COUNT RATHER THAN A COUNT OF ZERO. Selecting the
  // `contacts` chip must not make the `messages` chip claim zero results.
  if (!params.enabled || !params.loaded) return { kind: 'unknown' }
  return { kind: 'known', n: params.n }
}

/**
 * Where a message result navigates to.
 *
 * ⛔ `Build_Order:490`: *"A MESSAGE RESULT OPENS ITS CONVERSATION SCROLLED TO
 * THAT MESSAGE, highlighted."* So the target is a conversation AND a message,
 * and the message half cannot be dropped - opening the conversation at the
 * bottom and leaving somebody to scroll is the failure this line names.
 *
 * ⚠️ IT RETURNS THE PAIR RATHER THAN A URL, because `routing.ts` owns paths.
 * A second place that builds `/inbox/<id>` is a second place to get it wrong.
 */
export type ResultTarget =
  | { kind: 'conversation'; conversationId: string; messageId: string }
  | { kind: 'contact'; conversationId: string }
  /**
   * ⛔ A CONTACT WITH NO CONVERSATION HAS NOWHERE TO GO, and the schema permits
   * it. Returning a target that cannot be navigated to would make the row look
   * clickable and do nothing.
   */
  | { kind: 'none' }

export function contactTarget(hit: ContactHit): ResultTarget {
  return hit.conversationId === null
    ? { kind: 'none' }
    : { kind: 'contact', conversationId: hit.conversationId }
}
